import {
  useGetLedgerResourcesMutation,
  useGetMaterialResourcesMutation,
} from '@Features/MaterialRequest/requests/MaterialRequisition/store/api/MaterialRequestApiSlice';
import {
  useGetActivitiesResourcesMutation,
  useGetPlantResourcesMutation,
  useGetRequisitionResourcesMutation,
} from '@Store/api/dictionaryApiSlice';
import {
  ActivityResources,
  LedgerResources,
  MaterialResources,
  PlantResources,
} from '@Types/api/common/dictionary';
import RequisitionResource from '@Types/api/common/dictionary/MaterialRequest/RequisitionResources';
import { RequestListSearchResponse } from '@Types/api/requestList';
import { ResourceType } from '../types/ResourceType';

interface FindResourceProps {
  searchValue: string;
  requisitionId?: number;
  ccId?: number;
  compId?: number;
}

interface ReturnValue {
  findResource: (
    props: FindResourceProps,
    type: ResourceType
  ) =>
    | Promise<RequestListSearchResponse<ActivityResources>>
    | Promise<RequestListSearchResponse<MaterialResources>>
    | Promise<RequestListSearchResponse<LedgerResources>>
    | Promise<RequestListSearchResponse<RequisitionResource>>
    | Promise<RequestListSearchResponse<PlantResources>>
    | undefined;
}

const useFindResources = (): ReturnValue => {
  const [findResourceCode] = useGetRequisitionResourcesMutation();
  const [findActivitiesCode] = useGetActivitiesResourcesMutation();
  const [findPlantsCode] = useGetPlantResourcesMutation();
  const [findMaterialsCode] = useGetMaterialResourcesMutation();
  const [findLedgersCode] = useGetLedgerResourcesMutation();

  const findResource = (
    params: FindResourceProps,
    type: ResourceType
  ):
    | Promise<RequestListSearchResponse<ActivityResources>>
    | Promise<RequestListSearchResponse<MaterialResources>>
    | Promise<RequestListSearchResponse<LedgerResources>>
    | Promise<RequestListSearchResponse<RequisitionResource>>
    | Promise<RequestListSearchResponse<PlantResources>>
    | undefined => {
    const { requisitionId, ccId, searchValue, compId } = params;
    if (type === 'Activities') {
      return findActivitiesCode({ searchValue }).unwrap();
    }

    if (type === 'Resources') {
      return findResourceCode({ ccId: ccId!, resource: searchValue }).unwrap();
    }

    if (type === 'Materials') {
      return findMaterialsCode({
        requisitionId: requisitionId!,
        searchValue,
      }).unwrap();
    }

    if (type === 'Ledgers') {
      return findLedgersCode({
        requisitionId: requisitionId!,
        searchValue,
      }).unwrap();
    }

    if (type === 'Plants') {
      return findPlantsCode({
        compId: compId!,
        searchValue,
      }).unwrap();
    }

    return undefined;
  };

  return {
    findResource,
  };
};

export default useFindResources;
